import React, { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useApp } from '../context/AppContext';
import { MockQRCode } from '../components/MockQRCode';
import { ArrowLeft, Copy, Calendar, Gift, Check } from 'lucide-react';
interface RewardDetailPageProps {
  claimedRewardId: string;
  onNavigate: (page: string) => void;
}
export function RewardDetailPage({
  claimedRewardId,
  onNavigate
}: RewardDetailPageProps) {
  const {
    claimedRewards
  } = useApp();
  const [copiedCoupon, setCopiedCoupon] = useState(false);
  const reward = claimedRewards.find(r => r.id === claimedRewardId);
  // Fallback if reward not found
  if (!reward) {
    return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-1152">
        <div className="flex-1 flex items-center justify-center p-6" data-id="element-1153">
          <div className="text-center" data-id="element-1154">
            <div className="text-5xl mb-4" data-id="element-1155">🎁</div>
            <h2 className="text-xl font-normal text-gray-900 mb-2" data-id="element-1156">
              Premio non trovato
            </h2>
            <p className="text-sm text-gray-500 mb-6" data-id="element-1157">
              Questo premio non esiste o è stato rimosso
            </p>
            <button onClick={() => onNavigate('market')} className="px-6 py-2.5 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-colors" data-id="element-1158">
              Torna ai premi
            </button>
          </div>
        </div>
      </div>;
  }
  const handleCopyCoupon = () => {
    navigator.clipboard.writeText(reward.couponCode);
    setCopiedCoupon(true);
    setTimeout(() => setCopiedCoupon(false), 2000);
  };
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-1159">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 px-6 py-4 flex items-center z-30 bg-white border-b border-gray-100" data-id="element-1160">
        <button onClick={() => onNavigate('market')} className="text-gray-900 hover:text-green-600 transition-colors" data-id="element-1161">
          <ArrowLeft className="h-6 w-6" strokeWidth={1.5} data-id="element-1162" />
        </button>
        <h1 className="flex-1 text-center text-lg font-normal text-gray-900 pr-6" data-id="element-1163">
          Il tuo premio
        </h1>
      </header>

      {/* Main Content */}
      <ScrollArea className="flex-1 pt-16" data-id="element-1164">
        <div className="px-6 py-8 max-w-md mx-auto space-y-6" data-id="element-1165">
          {/* Reward Logo */}
          {reward.logo && <div className="flex justify-center" data-id="element-1166">
              <img src={reward.logo} alt={reward.name} className="h-16 w-auto object-contain" data-id="element-1167" />
            </div>}

          {/* Reward Name */}
          <div className="text-center" data-id="element-1168">
            <h2 className="text-2xl font-normal text-gray-900 mb-2" data-id="element-1169">
              {reward.name}
            </h2>
            {reward.description && <p className="text-sm text-gray-600" data-id="element-1170">{reward.description}</p>}
          </div>

          {/* QR Code */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100" data-id="element-1171">
            <div className="flex justify-center mb-4" data-id="element-1172">
              <MockQRCode value={reward.qrValue} size={200} data-id="element-1173" />
            </div>
            <p className="text-xs text-center text-gray-500" data-id="element-1174">
              Mostra questo codice QR al momento dell'acquisto
            </p>
          </div>

          {/* Coupon Code */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100" data-id="element-1175">
            <div className="flex items-center justify-between mb-3" data-id="element-1176">
              <span className="text-sm font-normal text-gray-600" data-id="element-1177">
                Codice coupon
              </span>
              <button onClick={handleCopyCoupon} className="flex items-center gap-1 text-xs font-normal text-green-600 hover:text-green-700 transition-colors" data-id="element-1178">
                {copiedCoupon ? <>
                    <Check className="h-3.5 w-3.5" data-id="element-1179" />
                    Copiato!
                  </> : <>
                    <Copy className="h-3.5 w-3.5" data-id="element-1180" />
                    Copia
                  </>}
              </button>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-1181">
              <p className="text-2xl font-mono font-normal text-center text-gray-900 tracking-wider" data-id="element-1182">
                {reward.couponCode}
              </p>
            </div>
          </div>

          {/* Reward Details */}
          <div className="bg-white rounded-2xl p-6 border border-gray-100 space-y-4" data-id="element-1183">
            <div data-id="element-1184">
              <p className="text-xs text-gray-500 mb-1" data-id="element-1185">Valore</p>
              <p className="text-base font-normal text-gray-900" data-id="element-1186">
                €{reward.value}
              </p>
            </div>

            <div className="h-px bg-gray-100" data-id="element-1187" />

            <div data-id="element-1188">
              <p className="text-xs text-gray-500 mb-1" data-id="element-1189">Scadenza</p>
              <p className="text-base font-normal text-gray-900" data-id="element-1190">
                {formatDate(reward.expiresAt)}
              </p>
            </div>

            <div className="h-px bg-gray-100" data-id="element-1191" />

            <div data-id="element-1192">
              <p className="text-xs text-gray-500 mb-1" data-id="element-1193">Ottenuto il</p>
              <p className="text-base font-normal text-gray-900" data-id="element-1194">
                {formatDate(reward.claimedAt)}
              </p>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-1195">
            <p className="text-xs text-blue-900 leading-relaxed" data-id="element-1196">
              💡 <span className="font-medium" data-id="element-1197">Come usare:</span> Mostra il
              codice QR o inserisci il codice coupon al momento del pagamento
              per applicare lo sconto. Valido fino alla data di scadenza.
            </p>
          </div>

          {/* CTA Button */}
          <button onClick={() => onNavigate('market')} className="w-full py-3.5 px-4 rounded-full text-sm font-normal text-green-600 bg-white border-2 border-green-600 hover:bg-green-50 transition-all" data-id="element-1198">
            Torna ai premi
          </button>
        </div>
      </ScrollArea>
    </div>;
}